import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  Download,
  Search,
  Filter,
  Mail,
  Edit,
  Eye,
  Calendar,
  FileText,
  ExternalLink,
  RotateCcw,
} from "lucide-react";
import { adminStatsAPI, adminRevenueAPI } from "../../services/adminApi";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";

// Utility functions
const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

const formatPercent = (value) => `${value.toFixed(1)}%`;

// Color palettes
const CHART_COLORS = {
  primary: "#3B82F6",
  secondary: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  neutral: "#6B7280",
};

const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

// MetricCard Component
const MetricCard = ({
  title,
  value,
  change,
  trend,
  icon: Icon = DollarSign,
}) => {
  const isPositive = trend === "up";
  const changeColor = isPositive ? "text-green-600" : "text-red-600";
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          <div className={`flex items-center mt-2 ${changeColor}`}>
            <TrendIcon className="w-4 h-4 mr-1" />
            <span className="text-sm font-medium">{change}</span>
          </div>
        </div>
        <div className="p-3 bg-blue-50 rounded-full">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      </div>
    </div>
  );
};

// MetricsRow Component
const MetricsRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
    {children}
  </div>
);

// Chart wrapper with export functionality
const ChartContainer = ({ title, onExport, children }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <button
        onClick={onExport}
        className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900 border rounded-md hover:bg-gray-50"
      >
        <Download className="w-4 h-4 mr-1" />
        Export
      </button>
    </div>
    <div className="h-80">{children}</div>
  </div>
);

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg">
        <p className="font-semibold">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {formatter ? formatter(entry.value) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Customer Table Component
const CustomersTable = ({ customers, loading, onAction }) => {
  const [sortField, setSortField] = useState("mrr");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const filteredCustomers = useMemo(() => {
    return customers.filter(
      (customer) =>
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [customers, searchTerm]);

  const sortedCustomers = useMemo(() => {
    return [...filteredCustomers].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const multiplier = sortDirection === "asc" ? 1 : -1;

      if (typeof aVal === "string") {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return (aVal - bVal) * multiplier;
    });
  }, [filteredCustomers, sortField, sortDirection]);

  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedCustomers.slice(start, start + itemsPerPage);
  }, [sortedCustomers, currentPage]);

  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);

  const exportToCsv = () => {
    const headers = ["Name", "Email", "Tier", "Accounts", "MRR", "Status"];
    const csvContent = [
      headers.join(","),
      ...sortedCustomers.map((customer) =>
        [
          customer.name,
          customer.email,
          customer.tier,
          customer.accounts,
          customer.mrr,
          customer.status,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Customer Subscriptions
          </h3>
          <button
            onClick={exportToCsv}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button className="flex items-center px-3 py-2 border rounded-md hover:bg-gray-50">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: "name", label: "Customer" },
                { key: "email", label: "Email" },
                { key: "tier", label: "Tier" },
                { key: "accounts", label: "Accounts" },
                { key: "mrr", label: "MRR" },
                { key: "status", label: "Status" },
                { key: "actions", label: "Actions" },
              ].map(({ key, label }) => (
                <th
                  key={key}
                  onClick={() => key !== "actions" && handleSort(key)}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    key !== "actions" ? "cursor-pointer hover:bg-gray-100" : ""
                  }`}
                >
                  <div className="flex items-center">
                    {label}
                    {key !== "actions" && sortField === key && (
                      <span className="ml-1">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedCustomers.map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">
                    {customer.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                  {customer.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      customer.tier === "Premium"
                        ? "bg-purple-100 text-purple-800"
                        : customer.tier === "Standard"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {customer.tier}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                  {customer.accounts}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                  {formatCurrency(customer.mrr)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      customer.status === "Active"
                        ? "bg-green-100 text-green-800"
                        : customer.status === "Churned"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {customer.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onAction("view", customer.id)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onAction("edit", customer.id)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onAction("email", customer.id)}
                      className="text-green-600 hover:text-green-900"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-3 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, sortedCustomers.length)} of{" "}
              {sortedCustomers.length} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reports Section Component
const ReportsSection = ({ monthlyReport, quarterlyReport, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="space-y-3">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Monthly Report
          </h3>
          <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
            View Full Report <ExternalLink className="w-4 h-4 ml-1" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">New Subscriptions</span>
            <span className="font-semibold text-green-600">
              +{monthlyReport.newSubscriptions}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">Upgrades</span>
            <span className="font-semibold text-blue-600">
              +{monthlyReport.upgrades}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">Downgrades</span>
            <span className="font-semibold text-yellow-600">
              -{monthlyReport.downgrades}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">Cancellations</span>
            <span className="font-semibold text-red-600">
              -{monthlyReport.cancellations}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 pt-4 border-t-2">
            <span className="text-gray-900 font-semibold">Net MRR Change</span>
            <span
              className={`font-bold ${
                monthlyReport.netMrrChange >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {monthlyReport.netMrrChange >= 0 ? "+" : ""}
              {formatCurrency(monthlyReport.netMrrChange)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Quarterly Analysis
          </h3>
          <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
            View Forecast <ExternalLink className="w-4 h-4 ml-1" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">Growth Rate</span>
            <span
              className={`font-semibold ${
                quarterlyReport.growthRate >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {quarterlyReport.growthRate >= 0 ? "+" : ""}
              {formatPercent(quarterlyReport.growthRate)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">Avg Churn Rate</span>
            <span className="font-semibold text-red-600">
              {formatPercent(quarterlyReport.avgChurnRate)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">Revenue Forecast</span>
            <span className="font-semibold text-blue-600">
              {formatCurrency(quarterlyReport.revenueForecast)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b">
            <span className="text-gray-600">Customer Acquisition Cost</span>
            <span className="font-semibold text-gray-600">
              {formatCurrency(quarterlyReport.cac)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 pt-4 border-t-2">
            <span className="text-gray-900 font-semibold">Projected ARR</span>
            <span className="font-bold text-green-600">
              {formatCurrency(quarterlyReport.projectedArr)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Date Range Selector Component
const DateRangeSelector = ({ value, onChange }) => {
  const ranges = [
    { label: "Last 30 days", value: "30d" },
    { label: "Last 3 months", value: "3m" },
    { label: "Last 6 months", value: "6m" },
    { label: "Last 12 months", value: "12m" },
    { label: "Year to date", value: "ytd" },
    { label: "All time", value: "all" },
  ];

  return (
    <div className="flex items-center space-x-2 mb-6">
      <Calendar className="w-5 h-5 text-gray-500" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {ranges.map((range) => (
          <option key={range.value} value={range.value}>
            {range.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Main RevenueDashboard Component
const RevenueDashboard = () => {
  const [dateRange, setDateRange] = useState("3m");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    metrics: {
      mrr: 12450,
      arr: 149400,
      churnRate: 2.3,
      ltv: 1250,
    },
    revenueHistory: [],
    subscriptionDistribution: [],
    cohortData: [],
    paymentSuccess: [],
    customerGrowth: [],
    customers: [],
    reports: {
      monthly: {},
      quarterly: {},
    },
  });

  // Fetch revenue metrics
  const fetchRevenueMetrics = async () => {
    try {
      setLoading(true);
      
      // Try to fetch from API, but fallback to mock data for development
      let response;
      try {
        response = await adminRevenueAPI.getDashboard({ range: dateRange });
      } catch (apiError) {
        console.log('API not available, using mock data from service');
        // Use the mock data from the service directly
        response = await adminRevenueAPI.getDashboard({ range: dateRange });
      }

      // Transform the response data to match our structure
      const transformedData = {
        metrics: response.data.metrics || {
          mrr: 12450,
          arr: 149400,
          churnRate: 2.3,
          ltv: 1250,
        },
        revenueHistory:
          response.data.revenueHistory || generateMockRevenueHistory(),
        subscriptionDistribution: response.data.subscriptionDistribution || [
          { name: "Free", value: 45, color: PIE_COLORS[0] },
          { name: "Standard", value: 35, color: PIE_COLORS[1] },
          { name: "Premium", value: 20, color: PIE_COLORS[2] },
        ],
        cohortData: response.data.cohortData || generateMockCohortData(),
        paymentSuccess: response.data.paymentSuccess || [
          { name: "Successful", value: 94.5, color: CHART_COLORS.secondary },
          { name: "Failed", value: 5.5, color: CHART_COLORS.danger },
        ],
        customerGrowth:
          response.data.customerGrowth || generateMockCustomerGrowth(),
        customers: response.data.customers || generateMockCustomers(),
        reports: {
          monthly: response.data.monthlyReport || {
            newSubscriptions: 45,
            upgrades: 12,
            downgrades: 3,
            cancellations: 8,
            netMrrChange: 2340,
          },
          quarterly: response.data.quarterlyReport || {
            growthRate: 15.2,
            avgChurnRate: 2.8,
            revenueForecast: 185000,
            cac: 125,
            projectedArr: 178800,
          },
        },
      };

      setData(transformedData);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch revenue metrics:", error);
      // Use mock data on error
      setData({
        ...data,
        revenueHistory: generateMockRevenueHistory(),
        subscriptionDistribution: [
          { name: "Free", value: 45, color: PIE_COLORS[0] },
          { name: "Standard", value: 35, color: PIE_COLORS[1] },
          { name: "Premium", value: 20, color: PIE_COLORS[2] },
        ],
        cohortData: generateMockCohortData(),
        paymentSuccess: [
          { name: "Successful", value: 94.5, color: CHART_COLORS.secondary },
          { name: "Failed", value: 5.5, color: CHART_COLORS.danger },
        ],
        customerGrowth: generateMockCustomerGrowth(),
        customers: generateMockCustomers(),
        reports: {
          monthly: {
            newSubscriptions: 45,
            upgrades: 12,
            downgrades: 3,
            cancellations: 8,
            netMrrChange: 2340,
          },
          quarterly: {
            growthRate: 15.2,
            avgChurnRate: 2.8,
            revenueForecast: 185000,
            cac: 125,
            projectedArr: 178800,
          },
        },
      });
      setError("Failed to load revenue data, showing sample data");
    } finally {
      setLoading(false);
    }
  };

  // Generate mock data functions
  const generateMockRevenueHistory = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months.map((month, index) => ({
      month,
      mrr: 8000 + index * 500 + Math.random() * 1000,
      arr: (8000 + index * 500 + Math.random() * 1000) * 12,
    }));
  };

  const generateMockCohortData = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    return months.map((month, i) => ({
      cohort: month,
      month0: 100,
      month1: 85 - i * 2,
      month2: 75 - i * 3,
      month3: 68 - i * 4,
      month4: 62 - i * 5,
      month5: 58 - i * 6,
    }));
  };

  const generateMockCustomerGrowth = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months.map((month, index) => ({
      month,
      newCustomers: 15 + Math.floor(Math.random() * 20),
      churnedCustomers: 3 + Math.floor(Math.random() * 8),
      netGrowth: 12 + Math.floor(Math.random() * 15),
    }));
  };

  const generateMockCustomers = () => {
    return [
      {
        id: 1,
        name: "Acme Corp",
        email: "admin@acme.com",
        tier: "Premium",
        accounts: 25,
        mrr: 499,
        status: "Active",
      },
      {
        id: 2,
        name: "TechStart",
        email: "contact@techstart.io",
        tier: "Standard",
        accounts: 10,
        mrr: 99,
        status: "Active",
      },
      {
        id: 3,
        name: "Design Studio",
        email: "hello@designstudio.com",
        tier: "Premium",
        accounts: 15,
        mrr: 299,
        status: "Active",
      },
      {
        id: 4,
        name: "Marketing Inc",
        email: "team@marketing.com",
        tier: "Standard",
        accounts: 8,
        mrr: 99,
        status: "Trial",
      },
      {
        id: 5,
        name: "Small Business",
        email: "owner@smallbiz.com",
        tier: "Free",
        accounts: 3,
        mrr: 0,
        status: "Active",
      },
      {
        id: 6,
        name: "Enterprise Co",
        email: "admin@enterprise.com",
        tier: "Premium",
        accounts: 50,
        mrr: 999,
        status: "Active",
      },
      {
        id: 7,
        name: "Startup XYZ",
        email: "founder@startupxyz.com",
        tier: "Standard",
        accounts: 12,
        mrr: 149,
        status: "Active",
      },
      {
        id: 8,
        name: "Agency Plus",
        email: "contact@agencyplus.com",
        tier: "Premium",
        accounts: 30,
        mrr: 599,
        status: "Churned",
      },
    ];
  };

  useEffect(() => {
    fetchRevenueMetrics();
  }, [dateRange]);

  // Event handlers
  const handleCustomerAction = async (action, customerId) => {
    console.log(`${action} action for customer ${customerId}`);
    // Implement specific actions based on type
    switch (action) {
      case "view":
        // Navigate to customer detail page
        break;
      case "edit":
        // Open edit modal
        break;
      case "email":
        // Open email composer
        break;
      default:
        break;
    }
  };

  const handleExportChart = (chartName) => {
    // Implement chart export functionality
    console.log(`Exporting ${chartName} chart`);
  };

  const generateReport = async (type, dateRange) => {
    try {
      const response = await adminRevenueAPI.getReports({ type, ...dateRange });
      // Handle report generation
      console.log(`Generated ${type} report:`, response.data);
    } catch (error) {
      console.error("Failed to generate report:", error);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error} />
        <button
          onClick={fetchRevenueMetrics}
          className="mt-4 flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Revenue Dashboard
          </h1>
          <p className="text-gray-600">
            Monitor subscription revenue, growth metrics, and customer analytics
          </p>
        </div>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* Key Metrics */}
      <MetricsRow>
        <MetricCard
          title="MRR"
          value={formatCurrency(data.metrics.mrr)}
          change="+15%"
          trend="up"
          icon={DollarSign}
        />
        <MetricCard
          title="ARR"
          value={formatCurrency(data.metrics.arr)}
          change="+15%"
          trend="up"
          icon={Target}
        />
        <MetricCard
          title="Churn Rate"
          value={formatPercent(data.metrics.churnRate)}
          change="-0.5%"
          trend="down"
          icon={TrendingDown}
        />
        <MetricCard
          title="LTV"
          value={formatCurrency(data.metrics.ltv)}
          change="+8%"
          trend="up"
          icon={Users}
        />
      </MetricsRow>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend Chart */}
        <ChartContainer
          title="Revenue Trend (12 Months)"
          onExport={() => handleExportChart("revenue-trend")}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.revenueHistory}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={formatCurrency} />
              <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="mrr"
                stroke={CHART_COLORS.primary}
                strokeWidth={2}
                name="MRR"
              />
              <Line
                type="monotone"
                dataKey="arr"
                stroke={CHART_COLORS.secondary}
                strokeWidth={2}
                name="ARR"
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Subscription Distribution */}
        <ChartContainer
          title="Subscription Distribution"
          onExport={() => handleExportChart("subscription-distribution")}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data.subscriptionDistribution}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {data.subscriptionDistribution.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Payment Success Rate */}
        <ChartContainer
          title="Payment Success Rate"
          onExport={() => handleExportChart("payment-success")}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data.paymentSuccess}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {data.paymentSuccess.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Customer Growth */}
        <ChartContainer
          title="Customer Growth"
          onExport={() => handleExportChart("customer-growth")}
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.customerGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="newCustomers"
                stackId="1"
                stroke={CHART_COLORS.secondary}
                fill={CHART_COLORS.secondary}
                name="New Customers"
              />
              <Area
                type="monotone"
                dataKey="churnedCustomers"
                stackId="2"
                stroke={CHART_COLORS.danger}
                fill={CHART_COLORS.danger}
                name="Churned Customers"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>

      {/* Customer Table */}
      <CustomersTable
        customers={data.customers}
        loading={false}
        onAction={handleCustomerAction}
      />

      {/* Reports Section */}
      <ReportsSection
        monthlyReport={data.reports.monthly}
        quarterlyReport={data.reports.quarterly}
        loading={false}
      />
    </div>
  );
};

export default RevenueDashboard;
